import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { M365User } from "../types";

interface Team {
  id: string;
  display_name?: string;
  description?: string;
  visibility?: string;
  mail?: string;
}
interface Channel {
  id: string;
  display_name?: string;
  description?: string;
  membership_type?: string;
}
interface TeamMember {
  id: string;
  display_name?: string;
  upn?: string;
  role?: string;
}

// [settings group, Graph key, label] — common toggles surfaced in the UI.
const SETTING_TOGGLES: [string, string, string][] = [
  ["member_settings", "allowCreateUpdateChannels", "Members can create/edit channels"],
  ["member_settings", "allowDeleteChannels", "Members can delete channels"],
  ["member_settings", "allowAddRemoveApps", "Members can add/remove apps"],
  ["messaging_settings", "allowUserEditMessages", "Users can edit their messages"],
  ["messaging_settings", "allowUserDeleteMessages", "Users can delete their messages"],
  ["fun_settings", "allowGiphy", "Allow Giphy"],
  ["fun_settings", "allowStickersAndMemes", "Allow stickers & memes"],
];

/**
 * Microsoft Teams management. Scoped by the global customer switcher (no local
 * picker). Lists the tenant's teams; expanding one shows its channels and
 * owners/members. A team is backed by an M365 group, so add/remove member
 * reuses /api/v1/m365/{customer_id}/groups/{team_id}/members.
 */
export default function M365TeamsPanel({
  users,
  customerId,
}: {
  users: M365User[];
  customerId: number | null;
}) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [openTeam, setOpenTeam] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [addSel, setAddSel] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<any | null>(null);
  const [savingSetting, setSavingSetting] = useState("");

  const loadTeams = useCallback(() => {
    setOpenTeam(null);
    setChannels([]);
    setMembers([]);
    if (customerId == null) {
      setTeams([]);
      return;
    }
    setLoading(true);
    setError("");
    api
      .get(`/api/v1/m365/${customerId}/teams`)
      .then((r) => setTeams(r.data))
      .catch((e: unknown) => {
        setTeams([]);
        const err = e as { response?: { data?: { detail?: string } } };
        setError(
          err.response?.data?.detail ||
            "Couldn't load teams (is M365 configured, and has the tenant re-consented for Teams?)",
        );
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const refreshMembers = (tid: string) =>
    api
      .get(`/api/v1/m365/${customerId}/teams/${tid}/members`)
      .then((r) => setMembers(r.data))
      .catch(() => setMembers([]));

  const openTeamDetail = (tid: string) => {
    setOpenTeam(tid);
    setChannels([]);
    setMembers([]);
    setSettings(null);
    setAddSel("");
    setError("");
    api
      .get(`/api/v1/m365/${customerId}/teams/${tid}/channels`)
      .then((r) => setChannels(r.data))
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err.response?.data?.detail || "Couldn't load channels");
      });
    api
      .get(`/api/v1/m365/${customerId}/teams/${tid}/settings`)
      .then((r) => setSettings(r.data))
      .catch(() => setSettings(null));
    refreshMembers(tid);
  };

  // Toggle one setting via PATCH; optimistically update local state.
  const toggleSetting = async (tid: string, group: string, key: string, value: boolean) => {
    setSavingSetting(`${group}.${key}`);
    setError("");
    try {
      await api.patch(`/api/v1/m365/${customerId}/teams/${tid}/settings`, {
        [group]: { [key]: value },
      });
      setSettings((s: any) => ({ ...s, [group]: { ...(s?.[group] || {}), [key]: value } }));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Couldn't update setting");
    } finally {
      setSavingSetting("");
    }
  };

  const setArchived = async (tid: string, archived: boolean) => {
    if (!window.confirm(archived ? "Archive this team (makes it read-only)?" : "Unarchive this team?"))
      return;
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/teams/${tid}/archive`, { archived });
      setSettings((s: any) => ({ ...s, is_archived: archived }));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Couldn't change archive state");
    }
  };

  const addMember = async (tid: string) => {
    if (!addSel) return;
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/groups/${tid}/members`, {
        member_object_id: addSel,
      });
      setAddSel("");
      refreshMembers(tid);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Add member failed");
    }
  };

  const removeMember = async (tid: string, oid: string) => {
    setError("");
    try {
      await api.delete(`/api/v1/m365/${customerId}/groups/${tid}/members/${oid}`);
      refreshMembers(tid);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Remove member failed");
    }
  };

  const tenantUsers = users
    .filter((u) => u.customer_id === customerId)
    .sort((a, b) => (a.display_name || a.upn || "").localeCompare(b.display_name || b.upn || ""));
  const memberIds = new Set(members.map((m) => m.id));

  if (customerId == null) {
    return (
      <p className="text-sm text-gray-400 italic">
        Select a customer in the header to manage its Microsoft Teams.
      </p>
    );
  }

  return (
    <div>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading teams…</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
          {teams.length === 0 && (
            <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
              No teams found.
            </p>
          )}
          {teams.map((t) => (
            <div key={t.id}>
              <button
                onClick={() => (openTeam === t.id ? setOpenTeam(null) : openTeamDetail(t.id))}
                title={`${t.display_name || t.id}${t.description ? "\n" + t.description : ""}${t.visibility ? "\nVisibility: " + t.visibility : ""}${t.mail ? "\n" + t.mail : ""}`}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <span className="text-sm text-gray-800 dark:text-gray-100">
                  {t.display_name || t.id}
                  {t.visibility && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 capitalize">
                      {t.visibility}
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {openTeam === t.id ? "▲" : "▼"}
                </span>
              </button>
              {openTeam === t.id && (
                <div className="px-4 pb-3 bg-gray-50/50 dark:bg-gray-900/40">
                  <div className="grid gap-4 md:grid-cols-2">
                  {/* Channels */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                      Channels
                    </div>
                    {channels.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">
                        No channels.
                      </p>
                    ) : (
                      channels.map((ch) => (
                        <div key={ch.id} className="text-sm text-gray-700 dark:text-gray-200 py-0.5">
                          # {ch.display_name}
                          {ch.membership_type && ch.membership_type !== "standard" && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                              {ch.membership_type}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Members */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                      Owners &amp; members
                    </div>
                    <div className="space-y-1 mb-2">
                      {members.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">
                          No members.
                        </p>
                      ) : (
                        members.map((m) => (
                          <div
                            key={m.id}
                            className="flex items-center justify-between text-sm py-0.5"
                          >
                            <span className="text-gray-700 dark:text-gray-200">
                              {m.display_name || m.upn || m.id}
                              {m.role === "owner" && (
                                <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded-full ml-2">
                                  owner
                                </span>
                              )}
                            </span>
                            <button
                              onClick={() => removeMember(t.id, m.id)}
                              className="text-xs text-red-600 dark:text-red-400 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={addSel}
                        onChange={(e) => setAddSel(e.target.value)}
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Add a member…</option>
                        {tenantUsers
                          .filter((u) => !memberIds.has(u.object_id))
                          .map((u) => (
                            <option key={u.object_id} value={u.object_id}>
                              {u.display_name || u.upn}
                            </option>
                          ))}
                      </select>
                      <button
                        onClick={() => addMember(t.id)}
                        disabled={!addSel}
                        className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  </div>
                  {/* Settings */}
                  {settings && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          Team settings
                          {settings.is_archived && (
                            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full normal-case">
                              archived
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setArchived(t.id, !settings.is_archived)}
                          className="text-xs text-amber-700 dark:text-amber-400 hover:underline"
                        >
                          {settings.is_archived ? "Unarchive team" : "Archive team"}
                        </button>
                      </div>
                      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                        {SETTING_TOGGLES.map(([group, key, label]) => {
                          const busyKey = `${group}.${key}`;
                          return (
                            <label
                              key={busyKey}
                              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                            >
                              <input
                                type="checkbox"
                                checked={!!settings?.[group]?.[key]}
                                disabled={savingSetting === busyKey}
                                onChange={(e) => toggleSetting(t.id, group, key, e.target.checked)}
                              />
                              {label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
